import { useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./App.css";
import { Cursor } from "./components/Cursor";
import { Nav } from "./components/Nav";
import { Hero } from "./components/Hero";
import { Ticker } from "./components/Ticker";
import { Manifesto } from "./components/Manifesto";
import { Letter } from "./components/Letter";
import { Audience, Roadmap } from "./components/Audience";
import { Curriculum } from "./components/Curriculum";
import { HostSection } from "./components/HostSection";
import { Transformation } from "./components/Transformation";
import { Takeaways } from "./components/Takeaways";
import { FAQSection } from "./components/FAQSection";
import { FinalRegister } from "./components/FinalRegister";
import { Footer, StickyCTA } from "./components/Footer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import Replay from "./pages/Replay";
import ThankYou from "./pages/ThankYou";
import LandingV2 from "./pages/LandingV2";

gsap.registerPlugin(ScrollTrigger);

const Grain = () => (
  <svg className="grain-overlay" width="100%" height="100%">
    <filter id="grain">
      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" stitchTiles="stitch" />
    </filter>
    <rect width="100%" height="100%" filter="url(#grain)" />
  </svg>
);

function Landing() {
  const attribution = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source"),
      utm_medium: p.get("utm_medium"),
      utm_campaign: p.get("utm_campaign"),
      utm_content: p.get("utm_content"),
      utm_term: p.get("utm_term"),
      fbclid: p.get("fbclid"),
      gclid: p.get("gclid"),
      pageUrl: window.location.href,
      referrer: document.referrer || null,
    };
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenis.on("scroll", ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    const t = setTimeout(() => {
      gsap.utils.toArray("[data-reveal]").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 28 }, {
          opacity: 1, y: 0, duration: 0.8, ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 85%" },
        });
      });
      ScrollTrigger.refresh();
    }, 150);

    return () => {
      clearTimeout(t);
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative">
      <Grain />
      <Cursor />
      <Nav />
      <Hero attribution={attribution} />
      <Ticker />
      <Manifesto />
      <div data-reveal><Letter /></div>
      <div data-reveal><Audience /></div>
      <div data-reveal><Roadmap /></div>
      <div data-reveal><Curriculum /></div>
      <div data-reveal><HostSection /></div>
      <div data-reveal><Transformation /></div>
      <div data-reveal><Takeaways /></div>
      <div data-reveal><FAQSection /></div>
      <div data-reveal><FinalRegister attribution={attribution} /></div>
      <Footer />
      <StickyCTA />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingV2 />} />
        <Route path="/old" element={<Landing />} />
        <Route path="/replay" element={<Replay />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsConditions />} />
      </Routes>
    </BrowserRouter>
  );
}
